import { Fragment, useContext, useEffect, useState } from "react";
import { AppLayout } from "../../../../containers/layout/app";
import { AppContext } from "../../../../context/appContext";
import { EditLoanProductWrapper } from "./styled";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { AlertModalCheckIcon } from "../../../../assets";
import { useNavigate, useParams } from "react-router-dom";
import { retrieveLoanProductService } from "../../../../utils/api/configuration/loanproduct/retrieveLoanProduct";
import Cookies from "universal-cookie";
import {
	Box,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { BaseButton } from "../../../../components/button/styled";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllChartOfAccountService } from "../../../../utils/api/accounting/chartofaccount/retrieveAllChartOfAccount";
import { updateLoanProductService } from "../../../../utils/api/configuration/loanproduct/updateLoanProduct";

export const EditLoanProduct = () => {
	const options = ["yes", "no"];
	const terms = ["daily", "weekly", "monthly", "annually"];
    const statuses = ["active", "inactive"];
    // just need to add here that I have removed "reducing balance" from the array below for the mean time
	const interestTypes = ["flat rate"];
	const occupations = ["Engineer", "Banker", "Trader", "Others"];
	const settlementMethods = [
		"principal first",
		"interest first",
		"principal + interest",
	];
	const tabIndex = {
		start: 0,
		end: 2,
	};

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { id = "" } = useParams();
	const { setActiveNavItem } = useContext(AppContext);

	const [activeTab, setActiveTab] = useState(tabIndex.start);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [institutionId, setInstitutionId] = useState<string | null>(null);
	const [formDetails, setFormDetails] = useState({
		name: "",
		amount: "",
		interestRate: "",
		interestType: " ",
		term: " ",
		tenor: "",
		repaymentSettlementMethod: " ",
		glAccount: " ",
		suspenseGL: " ",
		interestIncomeGL: " ",
		interestReceivableGL: " ",
		suspenseInterestIncomeGL: " ",
		loanLostGL: " ",
		recoveryIncomeGL: " ",
		loanPenaltyGL: " ",
		collateralSavings: "",
		occupation: " ",
		allowOverride: " ",
		status: " ",
	});

	useEffect(() => {
		setActiveNavItem("Configuration");
	}, [setActiveNavItem]);

	useEffect(() => {
		if (!id) return;
		retrieveLoanProductService(TOKEN, id)
			.then(async (data) => {
				setInstitutionId(data.institution);
				return setFormDetails(data);
			})
			.catch((err) => {
				console.error("Failed to fetch loan product:", err);
			});
	}, [TOKEN, id]);

	const { data: assetGLs } = useQuery({
		queryKey: ["assetGLs", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllChartOfAccountService(TOKEN);
			return (
				response?.data?.filter(
					(gl: Record<string, any>) =>
						gl.glAccountType === "GL Account" && gl.glType === "100"
				) ?? []
			);
		},
		enabled: !!TOKEN,
	});

	const { data: liabilityGLs } = useQuery({
		queryKey: ["liabilityGLs", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllChartOfAccountService(TOKEN);
			return (
				response?.data?.filter(
					(gl: Record<string, any>) =>
						gl.glAccountType === "GL Account" && gl.glType === "200"
				) ?? []
			);
		},
		enabled: !!TOKEN,
	});

	const { data: incomeGLs } = useQuery({
		queryKey: ["incomeGLs", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllChartOfAccountService(TOKEN);
			return (
				response?.data?.filter(
					(gl: Record<string, any>) =>
						gl.glAccountType === "GL Account" && gl.glType === "400"
				) ?? []
			);
		},
		enabled: !!TOKEN,
	});

	const { data: expenseGLs } = useQuery({
		queryKey: ["expenseGLs", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllChartOfAccountService(TOKEN);
			return (
				response?.data?.filter(
					(gl: Record<string, any>) =>
						gl.glAccountType === "GL Account" && gl.glType === "500"
				) ?? []
			);
		},
		enabled: !!TOKEN,
	});

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		setIsAlertModalOpen(false);
		return navigate(-1);
	};

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>
	) => {
		const { name, value } = e.target;
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
			...(name === "term" && { tenor: "" }),
		}));
	};

	const handlePagination = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		action: "previous" | "next"
	) => {
		e.preventDefault();
		const form = document.querySelector("form") as HTMLFormElement | null;
		switch (action) {
			case "previous":
				activeTab === tabIndex.start
					? setActiveTab((prev) => prev)
					: setActiveTab((prev) => prev - 1);
				break;
			case "next":
				if (form && !form.reportValidity()) return;
				activeTab === tabIndex.end - 1
					? await handleSubmit(e)
					: setActiveTab((prev) => prev + 1);
				break;
			default:
				break;
		}
	};

	const handleSubmit = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>
	) => {
		e.preventDefault();
		setError(null);
		if (!institutionId) return;
		setIsLoading(true);
		try {
			const transformedPayload = {
				...formDetails,
				interestRate: String(formDetails.interestRate).replace(/%/g, "").trim(),
				collateralSavings: String(formDetails.collateralSavings)
					.replace(/%/g, "")
					.trim(),
			};
			const response = await updateLoanProductService(
				TOKEN,
				institutionId,
				id,
				transformedPayload
			);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
			} else {
				setIsLoading(false);
				setError(
					"Failed to update loan product. Please check credentials and try again."
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to update loan product. ${error.message}`);
			console.error("Failed to update loan product:", error);
		}
	};

	return (
		<AppLayout pageId={"Loan-Products"} pageTitle={"Edit Loan Product"}>
			<EditLoanProductWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalCheckIcon />}
					title="Loan Product Updated Successfully"
					message="Your information has been successfully updated."
					callToAction="Back to Loan Product List"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<Typography
						variant="h3"
						fontFamily={"Poppins"}
						fontWeight={500}
						fontSize={18}
						lineHeight={"normal"}
						color="var(--text-salmon-pink-color)"
					>
						Enter required information to update loan product
					</Typography>
				</Box>
				<form>
					<Grid2
						container
						component={"div"}
						spacing={"var(--flex-gap)"}
						className="fieldset-grid-wrapper"
					>
						{activeTab === tabIndex.start && (
							<Fragment>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Loan Product Name
										</BaseLabel>
										<BaseInput
											required
											name="name"
											value={formDetails.name}
											placeholder="Enter Loan Product Name"
											onChange={(e) => handleChange(e)}
										/>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Loan Amount
										</BaseLabel>
										<BaseInput
											required
											name="amount"
											value={formDetails.amount}
											placeholder="Enter Amount"
											onChange={(e) => handleChange(e)}
										/>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Interest Rate (Monthly)
										</BaseLabel>
										<BaseInput
											required
											name="interestRate"
											value={formDetails.interestRate}
											placeholder="Enter Year Interest Rate (%)"
											onChange={(e) => handleChange(e)}
										/>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Interest Type
										</BaseLabel>
										<BaseSelect
											name="interestType"
											fontweight={400}
											value={formDetails.interestType}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value=" ">Select Interest Type</BaseOption>
											{interestTypes?.map(
												(interestType: string, index: number) => (
													<BaseOption key={index} value={interestType}>
														{interestType.charAt(0).toUpperCase() +
															interestType.slice(1)}
													</BaseOption>
												)
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">Loan Term</BaseLabel>
										<BaseSelect
											name="term"
											fontweight={400}
											value={formDetails.term}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value=" ">Select Term</BaseOption>
											{terms?.map((term: string, index: number) => (
												<BaseOption key={index} value={term}>
													{term.charAt(0).toUpperCase() + term.slice(1)}
												</BaseOption>
											))}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">Loan Tenor</BaseLabel>
										<BaseInput
											required
											name="tenor"
											value={formDetails.tenor}
											placeholder="Input the duration of the loan term"
											onChange={(e) => handleChange(e)}
										/>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Repayment Settlement Method
										</BaseLabel>
										<BaseSelect
											name="repaymentSettlementMethod"
											fontweight={400}
											value={formDetails.repaymentSettlementMethod}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value=" ">
												Select Repayment Settlement Method
											</BaseOption>
											{settlementMethods?.map(
												(method: string, index: number) => (
													<BaseOption key={index} value={method}>
														{method.charAt(0).toUpperCase() + method.slice(1)}
													</BaseOption>
												)
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Allow Override
										</BaseLabel>
										<BaseSelect
											name="allowOverride"
											fontweight={400}
											value={formDetails.allowOverride}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value=" ">Select Option</BaseOption>
											{options?.map((option: string, index: number) => (
												<BaseOption key={index} value={option}>
													{option.charAt(0).toUpperCase() + option.slice(1)}
												</BaseOption>
											))}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6, desktop: 4 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">Status</BaseLabel>
										<BaseSelect
											name="status"
											fontweight={400}
											value={formDetails.status}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value={" "}>Select Status</BaseOption>
											{statuses?.map((status: string, index: number) => {
												return (
													<BaseOption key={index} value={status}>
														{status.charAt(0).toUpperCase() +
															status.slice(1).toLowerCase()}
													</BaseOption>
												);
											})}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
							</Fragment>
						)}
						{activeTab === tabIndex.start + 1 && (
							<Fragment>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Loan Product GL
										</BaseLabel>
										<BaseSelect
											name="glAccount"
											fontweight={400}
											value={formDetails.glAccount}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value={" "}>Select GL Account</BaseOption>
											{assetGLs?.map(
												(gl: Record<string, any>, index: number) => {
													return (
														<BaseOption key={index} value={gl.id}>
															{gl.glName
																?.split(" ")
																.map(
																	(word: string) =>
																		word.charAt(0).toUpperCase() + word.slice(1)
																)
																.join(" ")}
														</BaseOption>
													);
												}
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Loan Product in Suspense GL
										</BaseLabel>
										<BaseSelect
											name="suspenseGL"
											fontweight={400}
											value={formDetails.suspenseGL}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value={" "}>Select Suspense GL</BaseOption>
											{liabilityGLs?.map(
												(gl: Record<string, any>, index: number) => {
													return (
														<BaseOption key={index} value={gl.id}>
															{gl.glName
																?.split(" ")
																.map(
																	(word: string) =>
																		word.charAt(0).toUpperCase() + word.slice(1)
																)
																.join(" ")}
														</BaseOption>
													);
												}
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Interest Income GL
										</BaseLabel>
										<BaseSelect
											name="interestIncomeGL"
											fontweight={400}
											value={formDetails.interestIncomeGL}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value={" "}>
												Select Interest Income GL
											</BaseOption>
											{incomeGLs?.map(
												(gl: Record<string, any>, index: number) => {
													return (
														<BaseOption key={index} value={gl.id}>
															{gl.glName
																?.split(" ")
																.map(
																	(word: string) =>
																		word.charAt(0).toUpperCase() + word.slice(1)
																)
																.join(" ")}
														</BaseOption>
													);
												}
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Interest Receivable GL
										</BaseLabel>
										<BaseSelect
											name="interestReceivableGL"
											fontweight={400}
											value={formDetails.interestReceivableGL}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value={" "}>
												Select Interest Receivable GL
											</BaseOption>
											{assetGLs?.map(
												(gl: Record<string, any>, index: number) => {
													return (
														<BaseOption key={index} value={gl.id}>
															{gl.glName
																?.split(" ")
																.map(
																	(word: string) =>
																		word.charAt(0).toUpperCase() + word.slice(1)
																)
																.join(" ")}
														</BaseOption>
													);
												}
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Interest Income in Suspense GL
										</BaseLabel>
										<BaseSelect
											name="suspenseInterestIncomeGL"
											fontweight={400}
											value={formDetails.suspenseInterestIncomeGL}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value={" "}>
												Select Interest Income in Suspense GL
											</BaseOption>
											{liabilityGLs?.map(
												(gl: Record<string, any>, index: number) => {
													return (
														<BaseOption key={index} value={gl.id}>
															{gl.glName
																?.split(" ")
																.map(
																	(word: string) =>
																		word.charAt(0).toUpperCase() + word.slice(1)
																)
																.join(" ")}
														</BaseOption>
													);
												}
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Loan Lost GL
										</BaseLabel>
										<BaseSelect
											name="loanLostGL"
											fontweight={400}
											value={formDetails.loanLostGL}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value={" "}>Select Loan Lost GL</BaseOption>
											{expenseGLs?.map(
												(gl: Record<string, any>, index: number) => {
													return (
														<BaseOption key={index} value={gl.id}>
															{gl.glName
																?.split(" ")
																.map(
																	(word: string) =>
																		word.charAt(0).toUpperCase() + word.slice(1)
																)
																.join(" ")}
														</BaseOption>
													);
												}
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Recovery Income GL
										</BaseLabel>
										<BaseSelect
											name="recoveryIncomeGL"
											fontweight={400}
											value={formDetails.recoveryIncomeGL}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value={" "}>
												Select Recovery Income GL
											</BaseOption>
											{incomeGLs?.map(
												(gl: Record<string, any>, index: number) => {
													return (
														<BaseOption key={index} value={gl.id}>
															{gl.glName
																?.split(" ")
																.map(
																	(word: string) =>
																		word.charAt(0).toUpperCase() + word.slice(1)
																)
																.join(" ")}
														</BaseOption>
													);
												}
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Loan Penalty GL
										</BaseLabel>
										<BaseSelect
											name="loanPenaltyGL"
											fontweight={400}
											value={formDetails.loanPenaltyGL}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value={" "}>
												Select Loan Penalty GL
											</BaseOption>
											{incomeGLs?.map(
												(gl: Record<string, any>, index: number) => {
													return (
														<BaseOption key={index} value={gl.id}>
															{gl.glName
																?.split(" ")
																.map(
																	(word: string) =>
																		word.charAt(0).toUpperCase() + word.slice(1)
																)
																.join(" ")}
														</BaseOption>
													);
												}
											)}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">
											Collateral Savings (%)
										</BaseLabel>
										<BaseInput
											required
											name="collateralSavings"
											value={formDetails.collateralSavings}
											placeholder="Enter Collateral Savings (%)"
											onChange={(e) => handleChange(e)}
										/>
									</BaseFieldSet>
								</Grid2>
								<Grid2 size={{ mobile: 12, tablet: 6 }}>
									<BaseFieldSet>
										<BaseLabel colour="var(--dark-color)">Occupation</BaseLabel>
										<BaseSelect
											name="occupation"
											fontweight={400}
											value={formDetails.occupation}
											colour="var(--semi-gray-color)"
											onChange={(e) => handleChange(e)}
										>
											<BaseOption value={" "}>Select Occupation</BaseOption>
											{occupations?.map((occupation: string, index: number) => {
												return (
													<BaseOption key={index} value={occupation}>
														{occupation}
													</BaseOption>
												);
											})}
										</BaseSelect>
									</BaseFieldSet>
								</Grid2>
							</Fragment>
						)}
					</Grid2>
					{error && activeTab === tabIndex.end - 1 && (
						<Typography
							fontFamily={"Inter"}
							fontWeight={"600"}
							fontSize={14}
							lineHeight={"normal"}
							color={"var(--text-salmon-pink-color)"}
							whiteSpace={"normal"}
						>
							{error}
						</Typography>
					)}
					<Stack
						gap={"calc(var(--flex-gap)/2)"}
						justifyContent={"space-between"}
						flexDirection={{ mobile: "row" }}
					>
						<Box overflow={"hidden"}>
							<BaseButton
								disableElevation
								variant="contained"
								disabled={activeTab === tabIndex.start}
								sx={{
									width: { mobile: "100%", miniTablet: "auto" },
								}}
								onClick={(e) => handlePagination(e, "previous")}
							>
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Previous
								</Typography>
							</BaseButton>
						</Box>
						<Box overflow={"hidden"}>
							<BaseButton
								disableElevation
								variant="contained"
								sx={{
									width: { mobile: "100%", miniTablet: "auto" },
								}}
								onClick={(e) => handlePagination(e, "next")}
							>
								{isLoading ? (
									<CircularProgress color="inherit" className="loader" />
								) : (
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										{activeTab === tabIndex.end - 1 ? "Submit" : "Next"}
									</Typography>
								)}
							</BaseButton>
						</Box>
					</Stack>
				</form>
			</EditLoanProductWrapper>
		</AppLayout>
	);
};
